import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {PAGE_URLS, SIGNIN_TYPES} from "constants/AppConstants.js";
import useSignIn from "hooks/useSignIn";
import {StyleButton} from "components/Common/StyleButton";
import {StyleDiv, StyleDivCenterChild} from "components/Common/StyleDiv";
import {StyleForm} from "components/Common/StyleForm";
import {StyleImg} from "components/Common/StyleImg";
import {StyleInputRoundIconWrapper} from "components/Common/StyleInput";
import {StyleLabel, StyleLabelWithSideLine} from "components/Common/StyleLabel";
import {StyleLink} from "components/Common/StyleLink";
import {SignInButton} from "components/SignIn/SignInButton";
import {SignInFormInput} from "components/SignIn/SignInFormInput";
import {ws} from "styles/tokens";

interface Props {
  signUp?: boolean;
}

function SignInForm({signUp}: Props) {
  const {handlerSignIn} = useSignIn();
  const {t} = useTranslation();
  const {
    register,
    handleSubmit,
    formState: {errors}
  } = useForm();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [confirmPasswordNotMatch, setConfirmPasswordNotMatch] = useState(false);

  async function handleEmailAndPasswordSignIn({email, password, confirmPassword, name}) {
    setConfirmPasswordNotMatch(false);
    if (signUp) {
      if (password !== confirmPassword) {
        setConfirmPasswordNotMatch(true);
        return;
      }
    }
    const credential = {email, password, name};
    await handlerSignIn(SIGNIN_TYPES.emailAndPassword, credential, signUp);
  }

  return (
    <StyleForm
      width="90%"
      maxWidth="400px"
      tabletMaxWidth="600px"
      onSubmit={handleSubmit(handleEmailAndPasswordSignIn)}
    >
      <StyleDiv margin="50px 0px 50px 0px">
        <StyleLabelWithSideLine>{t("auth.orContinueWith")}</StyleLabelWithSideLine>
      </StyleDiv>
      {signUp && (
        <StyleDiv margin="1.5rem 0px 1.5rem 0px">
          <StyleInputRoundIconWrapper className="inputWithIcon">
            <SignInFormInput
              {...register("name", {required: true})}
              height="50px"
              type="name"
              name="name"
              placeholder={t("auth.name")}
            />
            <StyleDiv className="left-icon">
              <StyleImg src="/assets/icons/icon-mail.svg" alt="mail icon" />
            </StyleDiv>
          </StyleInputRoundIconWrapper>
          {errors.name && (
            <StyleLabel fontSize="0.8rem" color={ws.danger}>
              {t("auth.nameRequired")}
            </StyleLabel>
          )}
        </StyleDiv>
      )}

      <StyleDiv margin="1.5rem 0px 1.5rem 0px">
        <StyleInputRoundIconWrapper className="inputWithIcon">
          <SignInFormInput
            {...register("email", {required: true})}
            height="50px"
            type="email"
            name="email"
            placeholder={t("auth.email")}
          />
          <StyleDiv className="left-icon">
            <StyleImg src="/assets/icons/icon-mail.svg" alt="user icon" />
          </StyleDiv>
        </StyleInputRoundIconWrapper>
        {errors.email && (
          <StyleLabel fontSize="0.8rem" color={ws.danger}>
            {t("auth.emailRequired")}
          </StyleLabel>
        )}
      </StyleDiv>

      <StyleDiv margin="1.5rem 0px 1.5rem 0px">
        <StyleInputRoundIconWrapper className="inputWithIcon">
          <SignInFormInput
            {...register("password", {required: true, minLength: 6})}
            height="50px"
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder={t("auth.password")}
          />
          <StyleDiv className="left-icon">
            <StyleImg marginBottom="20px" src="/assets/icons/icon-lock.svg" alt="lock icon" />
          </StyleDiv>
          <StyleButton type="button" className="right-icon" onClick={() => setShowPassword((ps) => !ps)}>
            <StyleImg
              src={showPassword ? "/assets/icons/icon-eye-on.svg" : "/assets/icons/icon-eye-off.svg"}
              alt="eye icon"
            />
          </StyleButton>
        </StyleInputRoundIconWrapper>
        {errors.password && (
          <StyleLabel fontSize="0.8rem" color={ws.danger}>
            {errors.password.type === "required" ? t("auth.passwordRequired") : t("auth.passwordMinLength")}
          </StyleLabel>
        )}
      </StyleDiv>

      {signUp && (
        <StyleDiv margin="1.5rem 0px 1.5rem 0px">
          <StyleInputRoundIconWrapper className="inputWithIcon">
            <SignInFormInput
              {...register("confirmPassword", {required: true})}
              height="50px"
              type={showPasswordConfirm ? "text" : "password"}
              name="confirmPassword"
              placeholder={t("auth.confirmPasswordPlaceholder")}
            />
            <StyleDiv className="left-icon">
              <StyleImg src="/assets/icons/icon-lock.svg" alt="lock icon" />
            </StyleDiv>
            <StyleButton type="button" className="right-icon" onClick={() => setShowPasswordConfirm((ps) => !ps)}>
              <StyleImg
                src={showPassword ? "/assets/icons/icon-eye-on.svg" : "/assets/icons/icon-eye-off.svg"}
                alt="eye icon"
              />
            </StyleButton>
          </StyleInputRoundIconWrapper>
          {confirmPasswordNotMatch && (
            <StyleLabel fontSize="0.8rem" color={ws.danger}>
              {t("auth.confirmPasswordNoMatch")}
            </StyleLabel>
          )}
        </StyleDiv>
      )}

      {!signUp && (
        <StyleLink to={PAGE_URLS.forgetPasswordPageUrl} float="right">
          {t("auth.forgotPasswordQuestion")}
        </StyleLink>
      )}

      <StyleDivCenterChild width="100%">
        <SignInButton>{signUp ? t("auth.signUp") : t("auth.signIn")}</SignInButton>
      </StyleDivCenterChild>
    </StyleForm>
  );
}

export default SignInForm;
